import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, RefreshCw, Building2, ExternalLink } from "lucide-react";

const STATUS_STYLE = {
  new: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
  contacted: "text-sky-400 border-sky-500/30 bg-sky-500/5",
  closed: "text-muted-foreground border-border bg-secondary/40",
};

export default function EnterpriseLeads() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enterprise-enquiry");
      setRows(data.entries);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="enterprise-leads-page">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow mb-2">Sales inbox</p>
          <h1 className="font-display text-2xl flex items-center gap-2">
            <Mail className="w-5 h-5" /> Enterprise leads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} enquiries captured from the pricing page's Contact us modal.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="enterprise-leads-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </header>

      {loading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bento p-10 text-center" data-testid="enterprise-leads-empty">
          <Building2 className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
          <p className="font-display text-lg mb-1">No enterprise leads yet</p>
          <p className="text-sm text-muted-foreground">Enquiries from the /pricing Enterprise CTA will show up here.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="enterprise-leads-list">
          {rows.map((r) => (
            <div key={r.id} className="bento p-5" data-testid={`enterprise-leads-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div>
                  <p className="font-medium">{r.name} · <span className="text-muted-foreground">{r.company}</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {r.industry} · {r.size.replace("-", "–")} employees · {new Date(r.submitted_at).toLocaleString()}
                  </p>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_STYLE[r.status] || STATUS_STYLE.new}`}>
                  {r.status}
                </span>
              </div>
              {r.message && (
                <p className="text-sm mt-3 pt-3 border-t border-border/40 text-muted-foreground leading-relaxed">
                  {r.message}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`mailto:${r.email}?subject=Re:%20Employee%20Brain%20Enterprise%20enquiry`}
                   className="inline-flex items-center gap-1.5 text-xs font-mono text-sky-500 hover:underline"
                   data-testid={`enterprise-leads-mailto-${r.id}`}>
                  {r.email} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
